import elephantLogo from "$assets/icons/elephant-invoice-icon.svg?url";
import { useBillingCurrency } from "$global-providers/billing-provider/provider";
import { useConvertFromPenceFactory } from "$routes/_lib/use-convert-currency";
import useCurrencyFormat from "$routes/_lib/use-currency-format";
import { Box, makeStyles, Typography } from "@material-ui/core";
import dayjs from "dayjs";
import { TransactionType } from "elephant-web/gql";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";

const useStyles = makeStyles((theme) => ({
  container: {
    margin: `${theme.spacing(1)}px 0`,
    padding: `${theme.spacing(1)}px 0`,
    float: "right",
    width: "180px",
  },
  total: {
    textAlign: "right",
    fontSize: "14px",
    justifyContent: "flex-end",
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  totalLarge: {
    textAlign: "right",
    fontSize: "14px",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(1),
  },
  logoContainer: {
    position: "absolute",
    bottom: 0,
    left: "calc(50% - 30px)",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    width: "60px",
    textAlign: "center",
    alignContent: "flex-start",
    marginTop: theme.spacing(2),
  },
  logoMsg: {
    fontSize: 10,
    width: "60px",
  },
  logo: {
    width: "100%",
  },
  cell: {
    width: "50%",
    display: "inline-block",
    textAlign: "right",
  },
}));

function getRebateTotal({ admission, discharge, dailyRebateAmount }) {
  const numberOfDays = dayjs(discharge).diff(dayjs(admission), "day", true);
  return dailyRebateAmount * numberOfDays;
}

const InvoicePrintSummary = ({
  invoice: {
    balance,
    payers,
    transactions,
    invoiceStatus,
    patientType,
    inpatientDetails,
  },
}) => {
  const classes = useStyles();
  const penceToPounds = useConvertFromPenceFactory();
  const currencyDetails = useBillingCurrency();
  const currencyFormatter = useCurrencyFormat(currencyDetails);
  const { formatMessage } = useIntl();
  const unpaid =
    invoiceStatus === "DRAFT" ||
    invoiceStatus === "UNPAID" ||
    invoiceStatus === "PARTIALLY_PAID";

  const includeRebateAmount =
    payers.find((p) => p.name === "NHIF") &&
    patientType === "Inpatient" &&
    inpatientDetails.dailyRebateAmount &&
    inpatientDetails.admission &&
    inpatientDetails.discharge;

  const totalWaived =
    transactions
      ?.filter((t) => t.payer.type === TransactionType.Waiver)
      .reduce((prevVal, curr) => prevVal + curr.amount.value, 0) ?? 0;

  return (
    <Box display="flex" justifyContent="flex-end" alignItems="center">
      <Box className={classes.logoContainer}>
        <img
          src={elephantLogo}
          alt={formatMessage({
            id: "printableInvoice.summary.logo",
            defaultMessage: "Elephant OS",
          })}
          className={classes.logo}
        />
        <Typography className={classes.logoMsg} type="body2">
          <FormattedMessage
            id="printableInvoice.summary.logo.text"
            defaultMessage="Elephant OS"
          />
        </Typography>
      </Box>
      <div className={classes.container}>
        <div className={classes.cell}>
          <Typography className={classes.total}>
            <strong>
              <FormattedMessage
                id="printableInvoice.summary.outstandingBalance"
                defaultMessage="Total:"
              />
            </strong>
          </Typography>
        </div>
        <div className={classes.cell}>
          <Typography className={classes.total}>
            <strong>{`${currencyFormatter?.format(
              penceToPounds(balance.total),
            )}`}</strong>
          </Typography>
        </div>
        {payers.map((payer) => (
          <>
            <div className={classes.cell}>
              <Typography className={classes.totalLarge} key={payer.type}>
                {payer.name === "PATIENT"
                  ? "Patient"
                  : payer.insuranceDetails?.name || payer.name}
                {":"}
              </Typography>
            </div>
            <div className={classes.cell}>
              <Typography className={classes.totalLarge} key={payer.type}>
                {currencyFormatter?.format(
                  penceToPounds(
                    unpaid ? payer.balance.total : payer.balance.paid,
                  ),
                )}
              </Typography>
            </div>
          </>
        ))}
        {totalWaived ? (
          <>
            <div className={classes.cell}>
              <Typography className={classes.totalLarge}>
                {"Waived:"}
              </Typography>
            </div>
            <div className={classes.cell}>
              <Typography className={classes.totalLarge}>
                {currencyFormatter?.format(penceToPounds(totalWaived))}
              </Typography>
            </div>
          </>
        ) : undefined}
        {includeRebateAmount && (
          <>
            <div className={classes.cell}>
              <Typography className={classes.totalLarge}>
                <FormattedMessage
                  id="printableInvoice.summary.rebate"
                  defaultMessage="Rebate:"
                />
              </Typography>
            </div>
            <div className={classes.cell}>
              <Typography className={classes.totalLarge}>
                {currencyFormatter?.format(getRebateTotal(inpatientDetails))}
              </Typography>
            </div>
          </>
        )}
      </div>
    </Box>
  );
};

InvoicePrintSummary.propTypes = {
  balance: PropTypes.shape({
    paid: PropTypes.number,
    due: PropTypes.number,
    total: PropTypes.number,
    currency: PropTypes.string,
  }).isRequired,
  payers: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.string,
      balance: PropTypes.shape({
        paid: PropTypes.number,
        due: PropTypes.number,
        total: PropTypes.number,
        currency: PropTypes.string,
      }),
    }),
  ).isRequired,
  invoiceStatus: PropTypes.string.isRequired,
};
export default InvoicePrintSummary;
